/**
 * WebMCP Debug Hooks — utilities for exposing tool handlers on window.__webmcpDebug
 * so developers can test tools without needing Chrome Canary.
 *
 * Debug hooks are only exposed in non-production environments, unless explicitly
 * enabled via window.__ENABLE_WEBMCP_DEBUG__ or localStorage "enableWebMcpDebug".
 *
 * These are plain functions (no Vue lifecycle hooks) so they can be used from
 * both Composition API composables and Options API component methods.
 */

const DEBUG_NAMESPACE = "__webmcpDebug";
const DEBUG_STORAGE_KEY = "enableWebMcpDebug";

/**
 * Returns true when debug hooks should be exposed on window.__webmcpDebug.
 * Always true outside production; in production, requires opt-in via
 * window.__ENABLE_WEBMCP_DEBUG__ or localStorage.
 */
export function shouldExposeDebugHook() {
  if (typeof window === "undefined") return false;
  if (process.env.NODE_ENV !== "production") return true;
  if (window.__ENABLE_WEBMCP_DEBUG__ === true) return true;

  try {
    return window.localStorage.getItem(DEBUG_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

/**
 * Registers a debug handler for a tool on window.__webmcpDebug[toolName].
 * No-op when debug hooks should not be exposed.
 *
 * @param {string} toolName
 * @param {function} handler
 */
export function registerDebugHook(toolName, handler) {
  if (!shouldExposeDebugHook()) return;
  if (!window[DEBUG_NAMESPACE]) window[DEBUG_NAMESPACE] = {};
  window[DEBUG_NAMESPACE][toolName] = handler;
}

/**
 * Removes a debug handler from window.__webmcpDebug[toolName].
 * Only removes the handler if it matches the provided reference (safe for
 * multiple components registering different handlers for different tools).
 * Cleans up the namespace object when it becomes empty.
 *
 * @param {string} toolName
 * @param {function} handler - the same reference passed to registerDebugHook
 */
export function unregisterDebugHook(toolName, handler) {
  if (typeof window === "undefined") return;

  const ns = window[DEBUG_NAMESPACE];
  if (!ns) return;

  if (ns[toolName] && ns[toolName] === handler) {
    delete ns[toolName];
  }

  if (!Object.keys(ns).length) {
    delete window[DEBUG_NAMESPACE];
  }
}
